import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Team, Sport } from '../types';
import { Position } from '../types/depth-chart';
import { 
  FOOTBALL_OFFENSIVE_FORMATIONS, 
  FOOTBALL_DEFENSIVE_FORMATIONS, 
  FOOTBALL_SPECIAL_TEAMS_FORMATIONS,
  FLAG_FOOTBALL_OFFENSIVE_FORMATIONS,
  SOCCER_FORMATIONS,
  Formation
} from '../data/sportsFormations';

interface UseFormationsProps {
  currentTeam: Team | null;
  activeTab: string;
  sportRuleset: any;
}

export const useFormations = ({ currentTeam, activeTab, sportRuleset }: UseFormationsProps) => {
  const [selectedFormation, setSelectedFormation] = useState<string>('');
  const [customFormations, setCustomFormations] = useState<{id: string, name: string, description?: string, positions: Position[]}[]>([]);

  // Function to get formations based on sport and tab
  const getFormationsForSportAndTab = useCallback((sport: Sport, tab: string): Formation[] => {
    console.log(`[useFormations] Getting formations for sport: ${sport}, tab: ${tab}`);
    let formations: Formation[] = [];
    
    switch (sport) {
      case 'Football':
        if (tab === 'Offense') {
          formations = FOOTBALL_OFFENSIVE_FORMATIONS;
        } else if (tab === 'Defense') {
          formations = FOOTBALL_DEFENSIVE_FORMATIONS;
        } else if (tab === 'Special Teams') {
          formations = FOOTBALL_SPECIAL_TEAMS_FORMATIONS;
        }
        break;
      case 'FlagFootball':
        if (tab === 'Offense') {
          formations = FLAG_FOOTBALL_OFFENSIVE_FORMATIONS;
        } else if (tab === 'Defense') {
          formations = [
            {
              name: 'Man Coverage',
              positions: [
                { key: 'CB1', name: 'CB1', fullName: 'Cornerback 1', color: '' },
                { key: 'CB2', name: 'CB2', fullName: 'Cornerback 2', color: '' },
                { key: 'S', name: 'S', fullName: 'Safety', color: '' },
                { key: 'LB1', name: 'LB1', fullName: 'Linebacker 1', color: '' },
                { key: 'LB2', name: 'LB2', fullName: 'Linebacker 2', color: '' }
              ]
            },
            {
              name: 'Zone Coverage',
              positions: [
                { key: 'CB1', name: 'CB1', fullName: 'Cornerback 1', color: '' },
                { key: 'CB2', name: 'CB2', fullName: 'Cornerback 2', color: '' },
                { key: 'S', name: 'S', fullName: 'Safety', color: '' },
                { key: 'LB1', name: 'LB1', fullName: 'Linebacker 1', color: '' },
                { key: 'LB2', name: 'LB2', fullName: 'Linebacker 2', color: '' }
              ]
            }
          ];
        }
        break;
      case 'Soccer':
        formations = SOCCER_FORMATIONS;
        break;
    }
    
    console.log(`[useFormations] Found ${formations.length} formations`);
    return formations;
  }, []);

  // Track which formations the team has selected
  const [teamSelectedFormations, setTeamSelectedFormations] = useState<string[]>([]);

  // Fetch team-selected formations
  const fetchTeamFormations = useCallback(async () => {
    if (!currentTeam) return [];
    
    try {
      const { data, error } = await supabase
        .from('team_formations')
        .select('formation_name, is_predefined, custom_formation_id')
        .eq('team_id', currentTeam.id)
        .eq('sport', currentTeam.sport)
        .eq('unit', activeTab);

      if (error) throw error;

      // Filter to only include:
      // 1. Predefined formations (is_predefined = true)
      // 2. Custom formations that have a custom_formation_id (new format)
      // This excludes old format formations that don't have custom_formation_id
      const validFormations = (data || []).filter(f => 
        f.is_predefined || f.custom_formation_id !== null
      );

      const formationNames = validFormations.map(f => f.formation_name);
      setTeamSelectedFormations(formationNames);
      return formationNames;
    } catch (error) {
      console.error('Error fetching team formations:', error);
      return [];
    }
  }, [currentTeam, activeTab]);

  // Get available formations for the current sport and tab
  // Only returns formations that the team has selected/created
  // Only shows formations that have a custom_formation_id (new format) or are predefined
  // Special exception: For Special Teams, automatically include all predefined formations
  const getAvailableFormations = useCallback((): {value: string, label: string}[] => {
    if (!currentTeam || !sportRuleset) return [];
    
    if (!sportRuleset.formations.enabled) return [];
    
    // Only show formations that the team has selected AND are in new format (have custom_formation_id) or are predefined
    const availableFormations: {value: string, label: string}[] = [];
    
    // For Special Teams, automatically include all predefined formations
    const isSpecialTeams = activeTab === 'Special Teams';
    
    // Add custom formations that are selected AND have a custom_formation_id (new format only)
    customFormations.forEach(formation => {
      // For Special Teams, show all custom formations. For other tabs, only show selected ones.
      if (isSpecialTeams || teamSelectedFormations.includes(formation.name)) {
        // Only show custom formations (no "(Custom)" label needed since all are custom now)
        availableFormations.push({
          value: formation.name,
          label: formation.name
        });
      }
    });
    
    // Add predefined formations
    // For Special Teams, automatically include all predefined formations
    // For other tabs, only show formations that are selected
    const customFormationNames = new Set(customFormations.map(f => f.name));
    const predefinedFormations = getFormationsForSportAndTab(currentTeam.sport, activeTab);
    predefinedFormations.forEach(formation => {
      const shouldInclude = isSpecialTeams 
        ? !customFormationNames.has(formation.name) // For Special Teams, include if no custom version exists
        : teamSelectedFormations.includes(formation.name) && !customFormationNames.has(formation.name); // For other tabs, only if selected
      
      if (shouldInclude) {
        availableFormations.push({
          value: formation.name,
          label: formation.name
        });
      }
    });
    
    return availableFormations.sort((a, b) => a.label.localeCompare(b.label));
  }, [currentTeam, sportRuleset, activeTab, customFormations, teamSelectedFormations, getFormationsForSportAndTab]);

  // Fetch custom formations from database (templates)
  const fetchCustomFormations = useCallback(async () => {
    if (!currentTeam) return [];
    
    try {
      console.log(`[useFormations] Fetching custom formations for team: ${currentTeam.name}, unit: ${activeTab}, sport: ${currentTeam.sport}`);
      
      // Get predefined formation names to exclude them
      const predefinedFormations = getFormationsForSportAndTab(currentTeam.sport, activeTab);
      const predefinedFormationNames = new Set(predefinedFormations.map(f => f.name));
      
      // Fetch custom formations from custom_formations table (new format)
      const { data: customFormationsData, error: customFormationsError } = await supabase
        .from('custom_formations')
        .select('id, name, description, positions')
        .eq('team_id', currentTeam.id)
        .eq('unit', activeTab)
        .eq('sport', currentTeam.sport)
        .order('name');

      if (customFormationsError) {
        console.error('Error fetching from custom_formations:', customFormationsError);
      }

      // Convert custom_formations data to the expected format
      const formationsFromCustomTable: {id: string, name: string, description?: string, positions: Position[]}[] = [];
      
      if (customFormationsData) {
        for (const formation of customFormationsData) {
          // Skip if it's a predefined formation name
          if (predefinedFormationNames.has(formation.name)) {
            continue;
          }

          // Convert positions from JSONB format to Position[] format
          const positions: Position[] = (formation.positions || []).map((pos: any) => ({
            key: (pos.label || pos.name || '').toUpperCase(),
            name: (pos.label || pos.name || '').toUpperCase(),
            fullName: pos.fullName || pos.full_name || pos.label || pos.name || '',
            color: pos.color || '',
            custom: true
          }));

          formationsFromCustomTable.push({
            id: formation.id,
            name: formation.name,
            description: formation.description || `Custom ${activeTab} formation`,
            positions: positions
          });
        }
      }

      // Only use new format from custom_formations table
      // Old format formations should be converted using the migration
      const allFormations = formationsFromCustomTable;
      
      console.log(`[useFormations] Found ${allFormations.length} custom formations:`, allFormations.map(f => f.name));
      setCustomFormations(allFormations);
      return allFormations;
    } catch (err) {
      console.error('Error fetching custom formations:', err);
      return [];
    }
  }, [currentTeam, activeTab, getFormationsForSportAndTab]);

  // Handle formation change
  const handleFormationChange = useCallback((formationName: string, onFormationSelect: (positions: Position[], formationName: string) => void) => {
    if (!currentTeam?.sport) return;
    
    console.log(`[useFormations] User selected formation: ${formationName}`);
    console.log(`[useFormations] Available custom formations:`, customFormations.map(f => ({ name: f.name, positionCount: f.positions.length })));
    console.log(`[useFormations] All custom formations:`, customFormations);
    setSelectedFormation(formationName);
    
    // For custom formations, get the positions from the formation definition
    const customFormation = customFormations.find(f => f.name === formationName);
    if (customFormation) {
      console.log(`[useFormations] ✅ Found custom formation with ${customFormation.positions.length} positions:`);
      console.log(`[useFormations] Custom formation positions:`, customFormation.positions);
      console.log(`[useFormations] Calling onFormationSelect with positions...`);
      onFormationSelect(customFormation.positions, formationName);
      return;
    }
    
    console.log(`[useFormations] ❌ Custom formation "${formationName}" not found in:`, customFormations.map(f => f.name));
    
    // For predefined formations, get the positions from the formation definition
    const predefinedFormations = getFormationsForSportAndTab(currentTeam.sport, activeTab);
    const predefinedFormation = predefinedFormations.find(f => f.name === formationName);
    
    if (predefinedFormation) {
      console.log(`[useFormations] Found predefined formation with ${predefinedFormation.positions.length} positions`);
      onFormationSelect(predefinedFormation.positions, formationName);
    } else {
      console.log(`[useFormations] Formation "${formationName}" not found in either custom or predefined formations`);
      onFormationSelect([], '');
    }
  }, [currentTeam, activeTab, customFormations, getFormationsForSportAndTab]);

  // Get default formation for current tab
  const getDefaultFormationForTab = useCallback((tab: string): string => {
    if (!currentTeam) return '';
    
    switch (tab) {
      case 'Offense':
        return currentTeam.defaultOffenseFormation || currentTeam.offensiveScheme || '';
      case 'Defense':
        return currentTeam.defaultDefenseFormation || currentTeam.defensiveScheme || '';
      case 'Special Teams':
        return currentTeam.defaultSpecialTeamsFormation || 'Kickoff';
      case 'Lineup':
        return currentTeam.defaultLineupFormation || '';
      case 'Fielding':
        return currentTeam.defaultFieldingFormation || '';
      default:
        return '';
    }
  }, [currentTeam]);

  // Check if current formation is the default for this tab
  const isDefaultFormation = useCallback((formationName: string): boolean => {
    const defaultFormation = getDefaultFormationForTab(activeTab);
    return defaultFormation === formationName;
  }, [getDefaultFormationForTab, activeTab]);

  // Set formation as default for current tab
  const setFormationAsDefault = useCallback(async (formationName: string, isDefault: boolean) => {
    if (!currentTeam) return;
    
    try {
      const columnMap: Record<string, string> = {
        'Offense': 'default_offense_formation',
        'Defense': 'default_defense_formation',
        'Special Teams': 'default_special_teams_formation',
        'Lineup': 'default_lineup_formation',
        'Fielding': 'default_fielding_formation'
      };
      
      const column = columnMap[activeTab];
      if (!column) {
        console.warn(`[useFormations] No default formation column mapping for tab: ${activeTab}`);
        return;
      }
      
      const updateData: Record<string, string | null> = {};
      updateData[column] = isDefault ? formationName : null;
      
      const { error } = await supabase
        .from('teams')
        .update(updateData)
        .eq('id', currentTeam.id);
      
      if (error) {
        // If column doesn't exist, log a warning but don't throw
        if (error.code === 'PGRST204' || error.message?.includes('column') || error.message?.includes('schema cache')) {
          console.warn(`[useFormations] Default formation column "${column}" does not exist. Please run migration 20250204000000_add_default_formation_columns.sql`);
          return;
        }
        throw error;
      }
      
      console.log(`[useFormations] ${isDefault ? 'Set' : 'Unset'} "${formationName}" as default for ${activeTab}`);
      
      // Update the team store with the new default formation setting
      // Import useTeamStore dynamically to avoid circular dependency issues
      const { useTeamStore } = await import('../store/teamStore');
      const { setCurrentTeam, currentTeam: storeCurrentTeam } = useTeamStore.getState();
      
      if (storeCurrentTeam) {
        // Map database column names to team object property names
        const propertyMap: Record<string, string> = {
          'default_offense_formation': 'defaultOffenseFormation',
          'default_defense_formation': 'defaultDefenseFormation',
          'default_special_teams_formation': 'defaultSpecialTeamsFormation',
          'default_lineup_formation': 'defaultLineupFormation',
          'default_fielding_formation': 'defaultFieldingFormation'
        };
        
        const propertyName = propertyMap[column];
        if (propertyName) {
          const updatedTeam = {
            ...storeCurrentTeam,
            [propertyName]: isDefault ? formationName : null
          };
          setCurrentTeam(updatedTeam);
        }
      }
      
    } catch (error) {
      console.error('Error setting default formation:', error);
    }
  }, [currentTeam, activeTab]);

  // Set default formation based on team settings
  // Only auto-select if there are selected formations available
  const setDefaultFormation = useCallback((onFormationSelect: (positions: Position[]) => void) => {
    if (!currentTeam || !sportRuleset || !sportRuleset.formations.enabled || selectedFormation !== '') {
      return;
    }

    const availableFormations = getAvailableFormations();
    
    // For Special Teams, formations are always available (predefined), so skip the teamSelectedFormations check
    const isSpecialTeams = activeTab === 'Special Teams';
    
    // Don't auto-select if no formations are available (unless it's Special Teams)
    if (!isSpecialTeams && teamSelectedFormations.length === 0) {
      console.log(`[useFormations] No formations selected by team - skipping auto-selection`);
      return;
    }
    
    // If no formations are available at all, skip
    if (availableFormations.length === 0) {
      console.log(`[useFormations] No formations available - skipping auto-selection`);
      return;
    }
    
    // If there's only one formation, auto-select it
    if (availableFormations.length === 1) {
      const singleFormation = availableFormations[0].value;
      console.log(`[useFormations] Only one formation available, auto-selecting: "${singleFormation}"`);
      setSelectedFormation(singleFormation);
      handleFormationChange(singleFormation, onFormationSelect);
      return;
    }
    
    // Otherwise, try to use the saved default formation
    const defaultFormation = getDefaultFormationForTab(activeTab);
    
    console.log(`[useFormations] Looking for default formation: "${defaultFormation}" in ${availableFormations.length} available formations`);
    
    if (defaultFormation && availableFormations.some(f => f.value === defaultFormation)) {
      console.log(`[useFormations] Setting default formation to: "${defaultFormation}"`);
      setSelectedFormation(defaultFormation);
      handleFormationChange(defaultFormation, onFormationSelect);
    } else {
      console.log(`[useFormations] Default formation "${defaultFormation}" not found in available formations`);
    }
  }, [currentTeam, sportRuleset, activeTab, selectedFormation, teamSelectedFormations, getAvailableFormations, getDefaultFormationForTab, handleFormationChange]);

  // Load custom formations and team formations when team or tab changes
  useEffect(() => {
    if (currentTeam) {
      fetchCustomFormations();
      fetchTeamFormations();
    }
  }, [currentTeam, activeTab, fetchCustomFormations, fetchTeamFormations]);

  // Expose fetchTeamFormations so it can be called after formation creation
  // This is already in the return statement, but making sure it's accessible

  // Add a formation to team's selected formations
  const addTeamFormation = useCallback(async (formationName: string, isPredefined: boolean, customFormationId?: string) => {
    if (!currentTeam) return false;
    
    try {
      const { error } = await supabase
        .from('team_formations')
        .insert({
          team_id: currentTeam.id,
          formation_name: formationName,
          is_predefined: isPredefined,
          custom_formation_id: customFormationId || null,
          sport: currentTeam.sport,
          unit: activeTab
        });

      if (error) {
        // If it's a unique constraint error, formation is already selected - that's okay
        if (error.code !== '23505') {
          throw error;
        }
      }

      // Refresh team formations list
      await fetchTeamFormations();
      return true;
    } catch (error) {
      console.error('Error adding team formation:', error);
      return false;
    }
  }, [currentTeam, activeTab, fetchTeamFormations]);

  return {
    selectedFormation,
    customFormations,
    getAvailableFormations,
    getFormationsForSportAndTab,
    handleFormationChange,
    setDefaultFormation,
    fetchCustomFormations,
    setSelectedFormation,
    isDefaultFormation,
    setFormationAsDefault,
    getDefaultFormationForTab,
    addTeamFormation,
    teamSelectedFormations,
    fetchTeamFormations
  };
}; 